import numeral from 'numeral';
import type React from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

// Constellation
import {
  AdornedInput,
  Adornment,
  Button,
  ButtonGroup,
  Heading,
  Input,
  Label,
  ModalDialog,
  Paragraph,
  Spacer,
  TextButton,
} from '@zillow/constellation';

// Actions
import { updateFilterAndNavigate } from 'app/shared/flux/actions/FilterActions';

// Misc / utils
import * as S from './styles';
import pathsManifest from 'app/shared/utils/pathsManifest';
import { logError } from '@zg-rentals/log-error';

const NoZestimateModal = ({ isOpen, onClose, address }: { isOpen: boolean; onClose: () => void; address: string }) => {
  const dispatch = useDispatch();
  const [inputPrice, updateInputPrice] = useState('');
  const [errorMsg, updateErrorMsg] = useState('');

  const handleCancel = () => {
    updateErrorMsg('');
    onClose();
  };

  const handlePriceChange = (e: React.FormEvent<HTMLInputElement>) => {
    updateErrorMsg('');

    // @ts-ignore https://github.com/desktop/desktop/issues/297
    const enteredValue = e.target.value;

    updateInputPrice(enteredValue ? numeral(enteredValue).format('0,0') : '');
  };

  const handleSubmit = () => {
    const price = numeral(inputPrice).value();
    if (price === null) {
      updateErrorMsg('Please enter an amount');
      return;
    }

    const partialFilterObj = { price };

    // @ts-ignore
    dispatch(updateFilterAndNavigate(`${pathsManifest.PriceMyRentalPageResults}`, partialFilterObj)).catch(
      (err: unknown) => {
        updateErrorMsg('There has been an error. Please try again.');
        logError({
          error: err,
          errorType: 'Filter',
          context: {
            details: 'Filter: Error handling filter price change',
            root: 'components/NoZestimateModal#handleSubmit',
          },
        });
      },
    );
  };

  return (
    <ModalDialog
      mediaQuery={{ xs: { fullScreen: true }, lg: { fullScreen: false } }}
      onClose={handleCancel}
      header={
        <Heading level={5}>
          Rent Zestimate<sup>®</sup> not found
        </Heading>
      }
      isOpen={isOpen}
      body={
        <div>
          <Paragraph marginTop="sm" marginBottom="xs">
            We’re unable to provide a Rent Zestimate<sup>®</sup> for <strong>{`${address}`}</strong>.
          </Paragraph>
          <Paragraph marginBottom="sm">
            Enter a custom price and we&#39;ll show you nearby rental listings closest to the price you choose.
          </Paragraph>
          <Label required>Custom price</Label>
          <AdornedInput
            error={errorMsg !== ''}
            input={<Input required onChange={handlePriceChange} placeholder="Enter amount" value={inputPrice} />}
            leftAdornment={<Adornment>$</Adornment>}
            marginTop="xs"
            // @ts-ignore
            size="lg"
          />
          {errorMsg !== '' && <S.InputError>{errorMsg}</S.InputError>}
          <Spacer marginTop="xs" marginBottom="sm" />
        </div>
      }
      footer={
        <ButtonGroup aria-label="See listings">
          <TextButton onClick={handleCancel}>Cancel</TextButton>
          <Button buttonType="primary" onClick={handleSubmit}>
            See listings
          </Button>
        </ButtonGroup>
      }
    />
  );
};

export default NoZestimateModal;
